import cn from "classnames"
import React, { createContext, useContext, useEffect, useRef } from "react"
import Swiper, { SwiperOptions } from "swiper"

import { Arrow } from "../Arrow/Arrow"
import styles from "./index.module.css"

type ContextType = {
  sliderEl?: React.MutableRefObject<null | HTMLDivElement>
  paginationEl?: React.MutableRefObject<null | HTMLDivElement>
  nextEl?: React.MutableRefObject<null | HTMLDivElement>
  prevEl?: React.MutableRefObject<null | HTMLDivElement>
}
const SliderContext = createContext<ContextType>({})

export const Pagination: React.FC<{ className?: string }> = ({ className }) => {
  const { paginationEl } = useContext(SliderContext)
  return <div ref={paginationEl} className={cn(className, styles.Pagination)} />
}

type ArrowType = {
  direction: "right" | "left"
  inCircle?: boolean
  className?: string
}

export const SliderArrow: React.FC<ArrowType> = ({ direction, inCircle, className }) => {
  const { nextEl, prevEl } = useContext(SliderContext)

  return (
    <Arrow
      ref={direction === "right" ? nextEl : prevEl}
      className={className}
      direction={direction}
      inCircle={inCircle}
    />
  )
}

type SlideType = {
  className?: string
}

const Slide: React.FC<SlideType> = ({ children, className, ...props }) => {
  return (
    <div className={cn(className, styles.Slide)} {...props}>
      {children}
    </div>
  )
}

type SlidesType<T> = {
  className?: string
  slideClassName?: string
  slides: T[]
  children(slide: T): JSX.Element
}

export const Slides: <T>(props: SlidesType<T>) => React.ReactElement<SlidesType<T>> = ({
  slides,
  children,
  className,
  slideClassName,
}) => {
  const { sliderEl } = useContext(SliderContext)

  return (
    <div ref={sliderEl} className={cn(styles.Container, className)}>
      <div className={styles.Wrapper}>
        {slides.map((slide, i) => (
          <Slide key={i} className={slideClassName} data-hash={i}>
            {children ? children(slide) : null}
          </Slide>
        ))}
      </div>
    </div>
  )
}

type SliderType = {
  className?: string
  params?: SwiperOptions
  paginationType?: "bullets" | "fraction"
}

const Slider: React.FC<SliderType> = ({ children, className, params, paginationType }) => {
  const { paginationEl, nextEl, prevEl, sliderEl } = useContext(SliderContext)

  useEffect(() => {
    if (!sliderEl?.current) return

    new Swiper(sliderEl?.current, {
      speed: 600,
      resistanceRatio: 0.5,
      roundLengths: true,
      a11y: {
        enabled: false,
      },
      pagination: {
        el: paginationEl?.current as HTMLElement,
        bulletClass: styles.Bullet,
        bulletActiveClass: "_active",
        currentClass: styles.Current,
        totalClass: styles.Total,
        clickable: true,
        type: paginationType,
      },
      navigation: {
        nextEl: nextEl?.current as HTMLElement,
        prevEl: prevEl?.current as HTMLElement,
      },
      ...params,
    })
  }, [nextEl, paginationEl, paginationType, params, prevEl, sliderEl])

  return <div className={cn(className, styles.Inner)}>{children}</div>
}

export const SliderWrapper: React.FC<SliderType> = ({ children, ...props }) => {
  const sliderEl = useRef(null)
  const paginationEl = useRef(null)
  const nextEl = useRef(null)
  const prevEl = useRef(null)
  // const arrowNextRef = useRef<Arrow>(null)

  return (
    <SliderContext.Provider value={{ nextEl, prevEl, sliderEl, paginationEl }}>
      <Slider {...props}>{children}</Slider>
    </SliderContext.Provider>
  )
}

// const SliderExample = () => {
//   return (
//     <SliderWrapper className={styles.Wrapper} params={{ effect: "fade" }}>
//       ({(arrowRef, paginationRef)} => (
//       <Slides slides={slideItems}>
//         {({ img, img_xs, caption }) => (
//           <div
//             className={styles.Item}
//             style={{ backgroundImage: `url(${isMobile ? img_xs : img})` }}
//           >
//             <Caption caption={caption} />
//           </div>
//         )}
//       </Slides>
//       <div className={styles.Bottom}>
//         <Pagination ref={paginationRef} className={styles.Pagination} />
//       </div>
//       <Arrow ref={arrowNextRef} {...arrowNextProps} className={styles.ArrowNext} direction="next" />
//       <Arrow ref={arrowPrevRef} className={styles.ArrowNext} direction="next" />
//       ))
//     </SliderWrapper>
//   )
// }
